import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./styles.css";

import LoadingScreen from "./LoadingScreen";

import HomePage from "./pages/Public/HomePage";
import SchedulePage from "./pages/Public/SchedulePage";
import ScoreboardPage from "./pages/Public/ScoreboardPage";
import SignUpPage from "./pages/Public/SignUpPage";
import CoachSignUpPage from "./pages/Public/CoachSignUpPage";
import RefSignUpPage from "./pages/Public/RefSignUpPage";
import SignUpSelectPage from "./pages/Public/SignUpSelectPage";
import TeamSchedulesPage from "./pages/Public/TeamSchedulesPage";
import CoachRankingsPage from "./pages/Public/CoachRankingsPage";
import ComplaintFormPage from "./pages/Public/ComplaintFormPage";
import BoardMembersPage from "./pages/Public/BoardMembersPage";
import FieldScoreboardPage from "./pages/Public/FieldScoreboardPage";
import FieldScoreboardMasterPage from "./pages/Public/FieldScoreboardMasterPage";
import RefLiveGamesPage from "./pages/Public/RefLiveGamesPage";

// LOGIN
import LoginSelectPage from "./pages/Public/LoginSelectPage";
import CoachLoginPage from "./pages/Auth/CoachLoginPage";
import RefLoginPage from "./pages/Auth/RefLoginPage";
import ParentLoginPage from "./pages/Auth/ParentLoginPage";
import AdminLoginPage from "./pages/Auth/AdminLoginPage";
import ResetPasswordPage from "./pages/Auth/ResetPasswordPage";

// REF APP
import RefLayout from "./layouts/RefLayout";
import RefDashboard from "./pages/Ref/RefDashboard";
import RefSchedule from "./pages/Ref/RefSchedule";
import RefTime from "./pages/Ref/RefTime";
import RefProfile from "./pages/Ref/RefProfile";
import RefAvailabilityPage from "./pages/Ref/RefAvailabilityPage";

import Dashboard from "./pages/Admin/Dashboard";

/* 🔥 ADDED COACH MANAGER */
import CoachManager from "./pages/Admin/CoachManager";

import PublicLayout from "./layouts/PublicLayout";
import AdminLayout from "./layouts/AdminLayout";

import { supabase } from "./supabase";

export default function App() {
  const [page, setPage] = useState(null);
  const [adminPage, setAdminPage] = useState("dashboard");
  const [accessDenied, setAccessDenied] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const init = async () => {
      let path = window.location.pathname.toLowerCase();

      if (!path || path === "" || path === "/index.html") {
        path = "/";
        window.history.replaceState({}, "", "/");
      }

      if (path === "/") setPage("home");
      else if (path === "/schedule") setPage("schedule");
      else if (path === "/scoreboard/ref") setPage("scoreboardRef");
      else if (path === "/scoreboard/brackets") setPage("scoreboardBrackets");
      else if (path === "/scoreboard/live") setPage("scoreboardLive");
      else if (path === "/scoreboard") setPage("scoreboard");
      else if (path === "/signup") setPage("signup");
      else if (path === "/coach-signup") setPage("coachSignup");
      else if (path === "/ref-signup") setPage("refSignup");
      else if (path === "/coach-rankings") setPage("coachRankings");
      else if (path === "/complaint") setPage("complaintForm");
      else if (path === "/board") setPage("boardMembers");
      else if (path === "/scoreboard-master") setPage("scoreboardMaster");
      else if (path.startsWith("/field-scoreboard/") && path.endsWith("/display/ref")) setPage("fieldScoreDisplayRef");
      else if (path.startsWith("/field-scoreboard/") && path.endsWith("/display/home")) setPage("fieldScoreDisplayHome");
      else if (path.startsWith("/field-scoreboard/") && path.endsWith("/display/away")) setPage("fieldScoreDisplayAway");
      else if (path.startsWith("/field-scoreboard/") && path.endsWith("/display")) setPage("fieldScoreDisplay");
      else if (path.startsWith("/field-scoreboard/") && path.endsWith("/control")) setPage("fieldScoreControl");
      else if (path.startsWith("/field-scoreboard/")) setPage("fieldScoreboard");
      else if (path === "/login") setPage("loginSelect");

      else if (path === "/ref-login") setPage("refLogin");
      else if (path === "/coach-login") setPage("coachLogin");
      else if (path === "/parent-login") setPage("parentLogin");
      else if (path === "/admin-login") setPage("adminLogin");
      else if (path === "/reset-password") setPage("resetPassword");

      /* 🔥 ADDED COACH ROUTE */
      else if (path === "/coach-manager") setPage("coachManager");

      else if (path.startsWith("/admin")) {
        await checkAdmin();
        setReady(true);
        return;
      }

      else if (path === "/ref" || path.startsWith("/ref/")) {
        await checkRef();
        setReady(true);
        return;
      }

      else setPage("home");

      setReady(true);
    };

    init();
  }, []);

  const checkAdmin = async () => {
    const {
      data: { user },
      error
    } = await supabase.auth.getUser();

    if (!user) {
      setPage("adminLogin");
      return;
    }

    const { data: userData, error: roleError } = await supabase
      .from("users")
      .select("is_admin")
      .eq("auth_id", user.id)
      .maybeSingle();

    if (roleError) {
      setPage("adminLogin");
      return;
    }

    if (!userData || !userData.is_admin) {
      setAccessDenied(true);
      setPage("home");
      return;
    }

    setAccessDenied(false);
    setPage("dashboard");
  };

  const checkRef = async () => {
    const { data } = await supabase.auth.getUser();

    if (!data.user) {
      setPage("refLogin");
      return;
    }

    setPage("refDashboard");
  };

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_, session) => {
        if (!session) {
          const path = window.location.pathname.toLowerCase();

          if (path.startsWith("/admin")) {
            setPage("adminLogin");
          }

          if (path === "/ref" || path.startsWith("/ref/")) {
            setPage("refLogin");
          }
        }
      }
    );

    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (page === "home") window.history.pushState({}, "", "/");
    if (page === "schedule") window.history.pushState({}, "", "/schedule");
    if (page === "scoreboardBrackets") window.history.pushState({}, "", "/scoreboard/brackets");
    if (page === "scoreboardLive") window.history.pushState({}, "", "/scoreboard/live");
    if (page === "scoreboard") window.history.pushState({}, "", "/scoreboard");
    if (page === "signup") window.history.pushState({}, "", "/signup");
    if (page === "coachSignup") window.history.pushState({}, "", "/coach-signup");
    if (page === "refSignup") window.history.pushState({}, "", "/ref-signup");
    if (page === "coachRankings") window.history.pushState({}, "", "/coach-rankings");
    if (page === "complaintForm") window.history.pushState({}, "", "/complaint");
    if (page === "boardMembers") window.history.pushState({}, "", "/board");
    if (page === "scoreboardMaster") window.history.pushState({}, "", "/scoreboard-master");
    if (page === "loginSelect") window.history.pushState({}, "", "/login");

    if (page === "refLogin") window.history.pushState({}, "", "/ref-login");
    if (page === "coachLogin") window.history.pushState({}, "", "/coach-login");
    if (page === "parentLogin") window.history.pushState({}, "", "/parent-login");
    if (page === "adminLogin") window.history.pushState({}, "", "/admin-login");

    if (page === "resetPassword") window.history.pushState({}, "", "/reset-password");

    /* 🔥 ADDED URL SYNC */
    if (page === "coachManager") window.history.pushState({}, "", "/coach-manager");

    if (page === "refDashboard") window.history.pushState({}, "", "/ref");
    if (page === "refSchedule") window.history.pushState({}, "", "/ref/schedule");
    if (page === "refTime") window.history.pushState({}, "", "/ref/time");
    if (page === "refAvailability") window.history.pushState({}, "", "/ref/availability");
    if (page === "refProfile") window.history.pushState({}, "", "/ref/profile");

    if (page === "dashboard") window.history.pushState({}, "", "/admin");
  }, [page]);

  if (!ready || page === null) return <LoadingScreen />;

  return (
    <>
      {accessDenied &&
        createPortal(
          <AccessDeniedModal onClose={() => setAccessDenied(false)} />,
          document.body
        )}

      {(page === "adminLogin" ||
        page === "refLogin" ||
        page === "coachLogin" ||
        page === "parentLogin" ||
        page === "resetPassword") && (
        <>
          {page === "adminLogin" && <AdminLoginPage />}
          {page === "refLogin" && <RefLoginPage setPage={setPage} />}
          {page === "coachLogin" && <CoachLoginPage setPage={setPage} />}
          {page === "parentLogin" && <ParentLoginPage setPage={setPage} />}
          {page === "resetPassword" && <ResetPasswordPage />}
        </>
      )}

      {page === "dashboard" && (
        <AdminLayout adminPage={adminPage} setAdminPage={setAdminPage}>
          <Dashboard adminPage={adminPage} setAdminPage={setAdminPage} />
        </AdminLayout>
      )}

      {/* 🔥 ADDED COACH MANAGER */}
      {page === "coachManager" && (
        <AdminLayout adminPage={adminPage} setAdminPage={setAdminPage}>
          <CoachManager />
        </AdminLayout>
      )}

      {page.startsWith("ref") &&
        page !== "refLogin" &&
        page !== "refSignup" && (
          <RefLayout page={page} setPage={setPage}>
            {page === "refDashboard" && <RefDashboard />}
            {page === "refSchedule" && <RefSchedule />}
            {page === "refTime" && <RefTime />}
            {page === "refAvailability" && <RefAvailabilityPage />}
            {page === "refProfile" && <RefProfile />}
          </RefLayout>
        )}

      {page === "fieldScoreboard" && <FieldScoreboardPage mode="master" />}
      {page === "fieldScoreControl" && <FieldScoreboardPage mode="control" />}
      {page === "fieldScoreDisplay" && <FieldScoreboardPage mode="display" />}
      {page === "fieldScoreDisplayHome" && <FieldScoreboardPage mode="displayHome" />}
      {page === "fieldScoreDisplayAway" && <FieldScoreboardPage mode="displayAway" />}
      {page === "fieldScoreDisplayRef" && <FieldScoreboardPage mode="displayRef" />}
      {page === "scoreboardMaster" && <FieldScoreboardMasterPage />}
      {page === "scoreboardRef" && <RefLiveGamesPage />}

      {page !== "dashboard" &&
        page !== "adminLogin" &&
        page !== "refLogin" &&
        page !== "coachLogin" &&
        page !== "parentLogin" &&
        page !== "resetPassword" &&
        page !== "coachManager" &&
        page !== "fieldScoreboard" &&
        page !== "fieldScoreControl" &&
        page !== "fieldScoreDisplay" &&
        page !== "fieldScoreDisplayHome" &&
        page !== "fieldScoreDisplayAway" &&
        page !== "fieldScoreDisplayRef" &&
        page !== "scoreboardMaster" &&
        page !== "scoreboardRef" &&
        (!page.startsWith("ref") ||
          page === "refSignup") && (
          <PublicLayout page={page} setPage={setPage}>
            {page === "home" && <HomePage setPage={setPage} />}
            {page === "schedule" && <SchedulePage setPage={setPage} />}
            {page === "scoreboard" && <ScoreboardPage />}
            {page === "scoreboardLive" && <ScoreboardPage initialLive />}
            {page === "scoreboardBrackets" && <ScoreboardPage initialBrackets />}
            {page === "teamSchedules" && <TeamSchedulesPage setPage={setPage} />}

            {page === "loginSelect" && <LoginSelectPage setPage={setPage} />}

            {page === "signupSelect" && <SignUpSelectPage setPage={setPage} />}
            {page === "signup" && <SignUpPage />}
            {page === "coachSignup" && <CoachSignUpPage />}
            {page === "refSignup" && <RefSignUpPage />}
            {page === "coachRankings" && <CoachRankingsPage />}
            {page === "complaintForm" && <ComplaintFormPage />}
            {page === "boardMembers" && <BoardMembersPage />}
          </PublicLayout>
        )}
    </>
  );
}
